import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import { FiFileText, FiMessageSquare } from "react-icons/fi";
import axios from "axios";
import { API_URL } from '../config/api';

const FileSummaryPage = () => {
	const location = useLocation();
	const { batch_id } = useParams();
	const [fileDetails, setFileDetails] = useState(location.state?.file || null);
	const [loading, setLoading] = useState(!fileDetails);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!fileDetails && batch_id) {
			const fetchFileById = async () => {
				setLoading(true);
				try {
					const response = await axios.get(
						`${API_URL}/get_file_by_batch_id.php?batch_id=${batch_id}`
					);
					if (response.data.status && response.data.data) {
						setFileDetails(response.data.data);
					} else {
						setError("File not found or an error occurred.");
					}
				} catch (err) {
					console.error("Error fetching file details:", err);
					setError("Failed to load file details.");
				} finally {
					setLoading(false);
				}
			};
			fetchFileById();
		} else if (!batch_id) {
			setError("No file ID provided.");
			setLoading(false);
		} else {
			setLoading(false);
		}
	}, [fileDetails, batch_id]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "80vh",
				}}
			>
				<CircularProgress sx={{ color: "red" }} />
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ textAlign: "center", mt: 4, color: "red" }}>
				<Typography variant='h6'>{error}</Typography>
			</Box>
		);
	}

	if (!fileDetails) {
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<Typography variant='h6'>No file details available.</Typography>
				<Typography variant='body2'>
					Please navigate from the uploaded files table.
				</Typography>
			</Box>
		);
	}

	const fixedRatePerRecipient = 4.9;
	const averageRateComponents = [4.9, 4.9, 4.9, 2.7];
	const fromRatePerRecipient =
		averageRateComponents.reduce((sum, current) => sum + current, 0) /
		averageRateComponents.length;

	const estimatedCostFrom = fileDetails?.total_count
		? (fileDetails.total_count * fromRatePerRecipient).toFixed(2)
		: "N/A";
	const estimatedCostTo = fileDetails?.total_count
		? (fileDetails.total_count * fixedRatePerRecipient).toFixed(2)
		: "N/A";

	return (
		<Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
			<Typography
				variant='h4'
				gutterBottom
				component='h1'
				sx={{ fontWeight: "bold", color: "red" }}
			>
				<FiFileText style={{ verticalAlign: "middle", marginRight: "8px" }} />
				File Details: {fileDetails.file_name || "N/A"}
			</Typography>

			<Paper
				elevation={3}
				sx={{
					p: 4,
					mt: 3,
					borderRadius: 2,
					backgroundColor: "white",
					border: "1px solid #f0f0f0",
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
						gap: 3,
					}}
				>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Batch ID:</strong>{" "}
						{fileDetails.batch_id}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Uploader:</strong>{" "}
						{fileDetails.full_name || "Unknown"}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Uploaded At:</strong>{" "}
						{fileDetails.created_at}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Status:</strong>{" "}
						{fileDetails.status}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Type:</strong>{" "}
						{fileDetails.type || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Category:</strong>{" "}
						{fileDetails.msg_cat || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Total Count:</strong>{" "}
						{fileDetails.total_count}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Total Distinct:</strong>{" "}
						{fileDetails.total_distinct}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Duplicates:</strong>{" "}
						{fileDetails.duplicates}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Sender ID:</strong>{" "}
						{fileDetails.senderid || "N/A"}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Scheduled For:</strong>{" "}
						{fileDetails.schedule_time || "Immediate"}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Cost Center:</strong>{" "}
						{fileDetails.cost_cntr || ""}
					</Typography>
					<Typography variant='body1'>
						<strong style={{ color: "red" }}>Estimated Cost:</strong>{" "}
						{estimatedCostFrom} - {estimatedCostTo}
					</Typography>
				</Box>
				<Box sx={{ mt: 4, borderTop: "1px solid #f0f0f0", pt: 3 }}>
					<Typography
						variant='h6'
						gutterBottom
						sx={{ color: "red", fontWeight: "bold" }}
					>
						<FiMessageSquare
							style={{ verticalAlign: "middle", marginRight: "8px" }}
						/>
						Message Content:
					</Typography>
					<Typography
						variant='body1'
						sx={{
							whiteSpace: "pre-wrap",
							backgroundColor: "#fff5f5", // light red background
							p: 2,
							borderRadius: 1,
							border: "1px solid #f0f0f0",
						}}
					>
						{fileDetails.message || "No message content available."}
					</Typography>
				</Box>
			</Paper>
		</Box>
	);
};

export default FileSummaryPage;
